import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { PostPreviewList, type PostPreviewItem } from '../../../../components/social/previews/PostPreviewList';
import { PostMediaView } from '../../../../components/social/previews/PostMediaView';
import { DESTINATIONS, availableDestinations, destinationById, type PostDestinationId } from '../../../../lib/publishing/destinations';
import {
  GBP_CTA_OPTIONS,
  clientMediaToPostMedia,
  composerSourceFromClientPost,
  toClubPostBody,
} from '../../../../lib/socialComposerAdapter';
import {
  effectiveCaption,
  effectiveMedia,
  emptyComposerState,
  fromPost,
  selectedDestinationIds,
  type ComposerState,
  type ComposerTarget,
} from '../../../publishing/composerState';
import { confirmSentence, PLATFORM_LABELS, postIsEditable, postStatusView } from '../../../../lib/socialPostView';
import type { PostMedia, PostPlatform } from '../../../../types/publishing';
import { CaptionWorkspaceDialog } from './CaptionWorkspaceDialog';
import { PlacementOverrides } from './PlacementOverrides';
import { socialApi, uploadSocialMedia, type ClientPost } from './socialApi';
import { SocialFrame } from './SocialFrame';
import { UploadMediaButton } from './UploadMediaButton';

// The composer. Photos/videos upload straight to storage; the caption can be
// written by hand or drafted by AI. ⚠️ AI IS ON-DEMAND ONLY: nothing here calls
// it from an effect or on render; only the buttons do. ⚠️ A schedule / publish-now
// goes through an explicit confirm step that states the club, the platforms and
// that it will be public, and only then sends `confirmed: true`.

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Composer({ accountId, existing }: { accountId: string; existing: ClientPost | null }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const accounts = useQuery({ queryKey: ['social-accounts'], queryFn: socialApi.accounts, retry: false });
  const account = accounts.data?.accounts.find((a) => a.id === accountId) ?? null;

  // ⚠️ The publishing composer's own state model (composerState), hosted here.
  const [state, setState] = useState<ComposerState>(() =>
    existing ? fromPost(composerSourceFromClientPost(existing)) : emptyComposerState(accountId),
  );
  const content = state.content;
  const media = state.media;
  const setContent = (v: string) => setState((st) => ({ ...st, content: v }));
  const setMedia = (f: (cur: PostMedia[]) => PostMedia[]) => setState((st) => ({ ...st, media: f(st.media) }));
  const setTarget = (id: PostDestinationId, patch: Partial<ComposerTarget>) =>
    setState((st) => ({ ...st, targets: { ...st.targets, [id]: { ...st.targets[id], ...patch } } }));
  const selected = selectedDestinationIds(state);
  const gbp = state.targets.googlebusiness.gbp;
  // The platforms the post reaches (a Story and a feed post on one platform count once).
  const platforms: PostPlatform[] = [...new Set(selected.map((d) => destinationById(d).platform))];
  const [when, setWhen] = useState(toLocalInput(existing?.scheduledFor ?? null));
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'schedule' | 'now' | null>(null);

  // AI workspace: a dialog, opened by a button. The brief and tone live here so they
  // survive closing it; everything else (drafts, the working caption) is the dialog's.
  const [aiOpen, setAiOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('');

  const readOnly = existing !== null && !postIsEditable(existing.status);

  useEffect(() => {
    // Default-tick every connected platform on a NEW post only.
    if (!existing && account && selected.length === 0) {
      setState((st) => {
        const targets = { ...st.targets };
        for (const d of availableDestinations(account.connected).filter((x) => x.placement === 'feed')) {
          targets[d.id] = { ...targets[d.id], enabled: true };
        }
        return { ...st, targets };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const body = (mode: 'draft' | 'schedule' | 'now', confirmed?: boolean) =>
    toClubPostBody(state, mode, mode === 'schedule' && when ? new Date(when).toISOString() : null, confirmed);

  const save = useMutation({
    mutationFn: (v: { mode: 'draft' | 'schedule' | 'now'; confirmed?: boolean }) =>
      existing ? socialApi.updatePost(existing.id, body(v.mode, v.confirmed)) : socialApi.createPost(body(v.mode, v.confirmed)),
    onSuccess: (res, v) => {
      void qc.invalidateQueries({ queryKey: ['social-posts'] });
      setConfirming(null);
      if (res.dispatchError) {
        // The post was kept as a draft; say so plainly rather than implying it went out.
        setNotice(`It did NOT publish: ${res.dispatchError} Your post was saved as a draft.`);
        return;
      }
      nav(`/portal/social/posts?account=${encodeURIComponent(accountId)}`, {
        state: { saved: v.mode },
      });
    },
    onError: (e) => {
      setConfirming(null);
      setNotice(errorText(e));
    },
  });

  // Upload, then hand the new items to `place`. The base picker and each
  // placement's own picker share this one uploader.
  const uploadThen = async (files: FileList | null, place: (items: PostMedia[]) => void) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setNotice(null);
    try {
      const out: PostMedia[] = [];
      for (const f of Array.from(files)) out.push(await uploadSocialMedia(f));
      place(out);
    } catch (e) {
      setNotice(errorText(e));
    } finally {
      setUploading(false);
    }
  };
  const upload = (files: FileList | null) => uploadThen(files, (items) => setMedia((m) => [...m, ...items]));
  const uploadForPlacement = (id: PostDestinationId, files: FileList | null) =>
    uploadThen(files, (items) =>
      setState((st) => ({
        ...st,
        targets: { ...st.targets, [id]: { ...st.targets[id], customMedia: [...st.targets[id].customMedia, ...items] } },
      })),
    );

  if (accounts.isPending) return <CenteredSpinner />;
  if (!account) return <p className="text-sm text-muted-foreground">This location cannot post yet. Ask Kyck to connect its pages.</p>;

  if (readOnly && existing) {
    const view = postStatusView(existing.status, existing.scheduledFor);
    return (
      <div className="max-w-2xl space-y-3">
        <p className="text-sm font-semibold text-foreground">{view.label}</p>
        <p className="text-sm text-muted-foreground">{view.sentence} A published post cannot be edited here.</p>
        <p className="whitespace-pre-wrap rounded-md border border-border p-3 text-sm text-foreground">{existing.content}</p>
        {existing.media.length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid="published-media">
            {existing.media.map((m) => (
              <div key={m.url} className="w-40">
                <PostMediaView media={[clientMediaToPostMedia(m)]} aspectClass="aspect-square" />
              </div>
            ))}
          </div>
        )}
        {existing.targets.map((t) => (
          <p key={t.platform} className="text-sm text-muted-foreground">
            {PLATFORM_LABELS[t.platform]}: {t.publishState ?? 'unknown'}
            {t.error ? ` (${t.error})` : ''}
          </p>
        ))}
      </div>
    );
  }

  // The live preview, one card per ticked destination, in the shared presentational
  // component. ⚠️ The Facebook link card needs a portal link-preview endpoint (not
  // built), so it is omitted here; everything else is what the public will see.
  // Each card shows that placement's EFFECTIVE caption and media, so an override
  // is visible exactly where it will publish.
  const previewItemsFor = (st: ComposerState): PostPreviewItem[] => selected.map((id) => ({
    destination: id,
    content: effectiveCaption(st, id),
    media: effectiveMedia(state, id),
    fbContentType: state.targets.facebook.fbContentType,
    igContentType: state.targets.instagram.igContentType,
    gbp: state.targets.googlebusiness.gbp,
    firstComment: '',
  }));
  const canPublish = platforms.length > 0 && content.trim() !== '' && !save.isPending && !uploading;
  const clubName = account.name;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,52.5rem)_22rem]">
    <div className="space-y-5">
      <section aria-label="Photos and videos" className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <UploadMediaButton onFiles={(f) => void upload(f)} uploading={uploading} />
          <span className="text-xs text-muted-foreground">Photos and videos go on every platform you pick.</span>
        </div>
        {media.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            {media.map((m, i) => (
              <div key={m.url} className="relative">
                {m.type === 'image' ? (
                  <img src={m.url} alt={m.altText ?? ''} className="h-20 w-20 rounded object-cover" />
                ) : (
                  <video src={m.url} className="h-20 w-20 rounded object-cover" />
                )}
                <button type="button" className="absolute -right-1 -top-1 rounded-full bg-background px-1 text-xs shadow" aria-label="Remove" onClick={() => setMedia((cur) => cur.filter((_, j) => j !== i))}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <fieldset>
        <legend className="text-sm font-medium text-foreground">Post to</legend>
        <div className="mt-1 flex flex-wrap gap-4">
          {DESTINATIONS.map((d) => {
            const connected = account.connected[d.platform];
            return (
              <label key={d.id} className={`flex items-center gap-2 text-sm ${connected ? '' : 'opacity-50'}`}>
                <input
                  type="checkbox"
                  disabled={!connected}
                  checked={state.targets[d.id].enabled}
                  onChange={(e) => setTarget(d.id, { enabled: e.target.checked })}
                />
                {d.label}
                {!connected && <span className="text-xs">(not connected)</span>}
              </label>
            );
          })}
        </div>
      </fieldset>

      {selected.length > 0 && (
        <section className="space-y-3 rounded-md border border-border p-3" aria-label="Placements" data-testid="placements">
          <h3 className="text-sm font-semibold text-foreground">Placements</h3>
          {state.targets.facebook.enabled && (
            <label className="flex items-center gap-2 text-sm">
              Facebook format
              <select
                aria-label="Facebook format"
                className="rounded-md border border-input bg-background px-2 py-1"
                value={state.targets.facebook.fbContentType === 'reel' ? 'reel' : 'feed'}
                onChange={(e) => setTarget('facebook', { fbContentType: e.target.value === 'reel' ? 'reel' : 'feed' })}
              >
                <option value="feed">Post</option>
                <option value="reel">Reel (video)</option>
              </select>
            </label>
          )}
          {state.targets.instagram.enabled && (
            <label className="flex items-center gap-2 text-sm">
              Instagram format
              <select
                aria-label="Instagram format"
                className="rounded-md border border-input bg-background px-2 py-1"
                value={state.targets.instagram.igContentType === 'reel' ? 'reel' : 'feed'}
                onChange={(e) => setTarget('instagram', { igContentType: e.target.value === 'reel' ? 'reel' : 'feed' })}
              >
                <option value="feed">Post</option>
                <option value="reel">Reel (video)</option>
              </select>
            </label>
          )}
          {state.targets.googlebusiness.enabled && (
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                Google button
                <select
                  aria-label="Google call to action"
                  className="rounded-md border border-input bg-background px-2 py-1"
                  value={gbp.cta?.type ?? ''}
                  onChange={(e) => {
                    const type = GBP_CTA_OPTIONS.find((c) => c.value === e.target.value)?.value;
                    setTarget('googlebusiness', { gbp: { ...gbp, cta: type ? { type, url: gbp.cta?.url ?? '' } : null } });
                  }}
                >
                  <option value="">No button</option>
                  {GBP_CTA_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              {gbp.cta && (
                <label className="block">
                  Button link
                  <input
                    aria-label="Google button link"
                    className="mt-1 w-full rounded-md border border-input bg-background p-2"
                    placeholder="https://"
                    value={gbp.cta.url}
                    onChange={(e) => {
                      const cta = gbp.cta;
                      if (cta) setTarget('googlebusiness', { gbp: { ...gbp, cta: { type: cta.type, url: e.target.value } } });
                    }}
                  />
                </label>
              )}
            </div>
          )}
          {(state.targets.facebook_story.enabled || state.targets.instagram_story.enabled) && (
            <p className="text-xs text-muted-foreground">Stories have no caption: only the photo or video is shown.</p>
          )}
        </section>
      )}

      <label className="block">
        <span className="text-sm font-medium text-foreground">Caption</span>
        <textarea
          className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm text-foreground"
          rows={8}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>

      <div className="flex flex-wrap items-center gap-2" data-testid="ai-entry">
        <Button type="button" variant="outline" onClick={() => setAiOpen(true)}>
          Write it with AI (optional)
        </Button>
        <span className="text-xs text-muted-foreground">Opens a workspace with your preview beside it. Nothing is sent to the AI until you press Draft.</span>
      </div>

      <PlacementOverrides state={state} setTarget={setTarget} onUpload={(id, f) => void uploadForPlacement(id, f)} uploading={uploading} />

      <label className="block">
        <span className="text-sm font-medium text-foreground">Schedule for (optional)</span>
        <input type="datetime-local" className="mt-1 rounded-md border border-input bg-background p-2 text-sm" value={when} onChange={(e) => setWhen(e.target.value)} />
      </label>

      {notice && (
        <p className="text-sm text-destructive" role="alert" data-testid="composer-notice">
          {notice}
        </p>
      )}

      {confirming ? (
        <div className="rounded-md border-2 border-tone-warning-foreground p-3" role="alertdialog" aria-label="Confirm publish" data-testid="confirm-publish">
          <p className="text-sm font-medium text-foreground">{confirmSentence(confirming, clubName, platforms, when ? new Date(when).toLocaleString() : null)}</p>
          <div className="mt-3 flex gap-2">
            <Button disabled={save.isPending} onClick={() => save.mutate({ mode: confirming, confirmed: true })}>
              {save.isPending ? 'Working…' : confirming === 'now' ? 'Yes, publish now' : 'Yes, schedule it'}
            </Button>
            <Button variant="outline" disabled={save.isPending} onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!canPublish} onClick={() => save.mutate({ mode: 'draft' })}>
            Save as draft
          </Button>
          <Button variant="outline" disabled={!canPublish || when === ''} onClick={() => setConfirming('schedule')}>
            Schedule
          </Button>
          <Button disabled={!canPublish} onClick={() => setConfirming('now')}>
            Publish now
          </Button>
        </div>
      )}
    </div>
    <aside aria-label="Preview" data-testid="post-preview">
      <h3 className="mb-2 text-sm font-semibold text-foreground">Preview</h3>
      <PostPreviewList items={previewItemsFor(state)} pageName={account.name} issues={[]} />
    </aside>
    {aiOpen && (
      <CaptionWorkspaceDialog
        accountId={accountId}
        platforms={platforms}
        media={media}
        caption={content}
        brief={brief}
        onBrief={setBrief}
        tone={tone}
        onTone={setTone}
        preview={(c) => <PostPreviewList items={previewItemsFor({ ...state, content: c })} pageName={account.name} issues={[]} />}
        onAccept={(c) => {
          // The BASE caption only: a per-platform caption the club set stays as typed.
          setContent(c);
          setAiOpen(false);
        }}
        onClose={() => setAiOpen(false)}
      />
    )}
    </div>
  );
}

export function ComposerPage() {
  const { postId } = useParams();
  const existing = useQuery({
    queryKey: ['social-post', postId],
    queryFn: () => socialApi.post(postId!),
    enabled: Boolean(postId),
    retry: false,
  });
  return (
    <SocialFrame tab="posts">
      {(accountId) =>
        postId ? (
          existing.isPending ? (
            <CenteredSpinner />
          ) : existing.isError ? (
            <p className="text-sm text-destructive" role="alert">
              That post was not found.
            </p>
          ) : (
            <Composer key={postId} accountId={existing.data.post.accountId} existing={existing.data.post} />
          )
        ) : (
          <Composer key={accountId} accountId={accountId} existing={null} />
        )
      }
    </SocialFrame>
  );
}
